import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff, AdminPanelSettings } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAuthStore } from '../store/authStore';
import authService from '../shared/services/authService';
import { colors } from '../theme';

const schema = yup.object().shape({
  username: yup.string().required('Username is required'),
  password: yup.string().required('Password is required'),
});

interface AdminLoginForm {
  username: string;
  password: string;
}

const AdminLogin: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string>('');

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AdminLoginForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      username: 'admin',
      password: 'admin123',
    },
  });

  const onSubmit = async (data: AdminLoginForm) => {
    try {
      setIsLoading(true);
      setLoginError('');
      
      const response = await authService.adminLogin(data.username, data.password);
      
      if (!response.user.is_admin) {
        setLoginError('Access denied. Admin privileges required.');
        return;
      }
      
      setAuth(response.user, response.token);
      toast.success('Welcome, Admin!');
      navigate('/admin/dashboard');
      
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Invalid credentials';
      setLoginError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #FFF8E1 0%, #FFE0B2 50%, #FFD180 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 5,
            width: '100%',
            maxWidth: 480,
            borderRadius: 4,
            background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
            border: '1px solid',
            borderColor: 'divider',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '6px',
              background: `linear-gradient(90deg, ${colors.gradientStart}, ${colors.gradientEnd}, ${colors.gradientStart})`,
              backgroundSize: '200% 100%',
              animation: 'gradientShift 3s ease infinite',
            },
            '@keyframes gradientShift': {
              '0%': { backgroundPosition: '0% 50%' },
              '50%': { backgroundPosition: '100% 50%' },
              '100%': { backgroundPosition: '0% 50%' },
            },
          }}
        >
          <Box textAlign="center" mb={4}>
            <Box
              sx={{
                display: 'inline-flex',
                p: 2,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
                boxShadow: '0 8px 24px rgba(242, 111, 33, 0.4)',
                mb: 2,
              }}
            >
              <AdminPanelSettings sx={{ fontSize: 56, color: 'white' }} />
            </Box>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                mb: 1,
              }}
            >
              Admin Portal
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontSize: '1rem',
              }}
            >
              Secure access to administrative dashboard
            </Typography>
          </Box>

          {loginError && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  color: theme.palette.error.main,
                },
              }}
            >
              {loginError}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Username"
                    variant="outlined"
                    error={!!errors.username}
                    helperText={errors.username?.message}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.paper',
                        '& fieldset': {
                          borderColor: 'divider',
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                          borderWidth: 2,
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'text.secondary',
                        '&.Mui-focused': {
                          color: theme.palette.primary.main,
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: 'text.primary',
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    variant="outlined"
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ color: theme.palette.primary.main }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.paper',
                        '& fieldset': {
                          borderColor: 'divider',
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                          borderWidth: 2,
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'text.secondary',
                        '&.Mui-focused': {
                          color: theme.palette.primary.main,
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: 'text.primary',
                      },
                    }}
                  />
                )}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isLoading}
                sx={{
                  mt: 2,
                  py: 1.5,
                  fontSize: '1.1rem',
                  background: `linear-gradient(135deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
                  color: 'white',
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(242, 111, 33, 0.4)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
                    filter: 'brightness(1.05)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(242, 111, 33, 0.5)',
                  },
                  '&:disabled': {
                    background: `linear-gradient(135deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
                    opacity: 0.55,
                    color: 'rgba(255, 255, 255, 0.8)',
                  },
                }}
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </Button>
            </Box>
          </form>

          <Box textAlign="center" mt={3}>
            <Button
              variant="text"
              onClick={() => navigate('/')}
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: theme.palette.primary.main,
                  textDecoration: 'underline',
                },
              }}
            >
              Back to Main Site
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AdminLogin;